"use client";

import React from "react";
import { motion } from "framer-motion";
import { UserCheck, PiggyBank, SlidersHorizontal as Sliders, Zap, ShieldCheck, Sparkles, ArrowRight, Check, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  id: 1,
  title: "Direct Senior Expertise",
  consultant: "You work directly with me—a senior consultant with proven hands-on technical and strategic experience.",
  agency: "Pitched by senior executives, then handed off to junior account managers learning on your dime.",
  accentLabel: "100% Direct",
  iconName: "UserCheck"
}, {
  id: 2,
  title: "Maximum Cost Efficiency",
  consultant: "Your entire investment funds active strategic execution, technical fixes, and content growth.",
  agency: "High monthly retainers inflated by agency overheads, account managers, and corporate office costs.",
  accentLabel: "Zero Overhead",
  iconName: "PiggyBank"
}, {
  id: 3,
  title: "Custom Tailored Strategy",
  consultant: "Bespoke campaigns custom-built for your business model, search landscape, and exact revenue goals.",
  agency: "Standardised monthly checklists and generic, copy-paste templates applied across all clients.",
  accentLabel: "Bespoke Approach",
  iconName: "Sliders"
}, {
  id: 4,
  title: "Agile & Immediate Speed",
  consultant: "Direct communication and fast implementation without bureaucratic red tape or multi-tier approvals.",
  agency: "Slow turnaround times with endless internal sign-offs and delays for minor site updates.",
  accentLabel: "Rapid Execution",
  iconName: "Zap"
}, {
  id: 5,
  title: "Transparent & Honest Reporting",
  consultant: "Direct, realistic measurement focused on qualified leads, organic sales, and real business impact.",
  agency: "Automated monthly PDFs packed with fluff metrics, keyword noise, and superficial statistics.",
  accentLabel: "true Accountability",
  iconName: "ShieldCheck"
}, {
  id: 6,
  title: "Modern AI & Search Strategy",
  consultant: "Agile implementation covering traditional organic rankings alongside modern AI Search Optimisation.",
  agency: "Slow to adapt to Search Generative Experiences, trapped in outdated decade-old agency playbooks.",
  accentLabel: "Future-Proof",
  iconName: "Sparkles"
}];
export default function Benefits() {
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden text-foreground">
      {}
      <div className="absolute top-0 right-1/4 -z-10 size-96 rounded-full bg-secondary/60 blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute bottom-10 left-10 -z-10 size-80 rounded-full bg-accent/10 blur-3xl opacity-50 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }}>
            <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-foreground border border-accent/20">
              Why Work Directly With Me
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif leading-tight">
            Independent SEO Consultant <br className="hidden sm:inline" />
            <span className="text-primary underline decoration-accent/40 decoration-wavy underline-offset-8">
              vs. Traditional SEO Agencies
            </span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you work with YE Consultancy, you don't get passed to an intern or account manager. 
            You get direct access to me, Yassine Essaadi—driving your search strategy with full clarity, 
            responsiveness, and accountability.
          </motion.p>
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-14 rounded-2xl bg-gradient-to-br from-secondary/80 via-card to-background p-6 sm:p-8 border border-accent/30 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                <Sparkles className="size-3.5" />
                <span>The Personal Guarantee</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                "You work directly with me. No middle layers, no generic templates."
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                I limit my active client roster to ensure every business receives my undivided strategic focus, 
                deep technical execution, and fast communication.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
              <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full border-border hover:bg-secondary">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {comparisonPoints.map((item, i) => {
          return <motion.div key={item.id} data-index={i} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="h-full">
                <Card className="h-full bg-card border-border/80 shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-accent/40 flex flex-col justify-between overflow-hidden">
                  <CardContent className="p-6 space-y-6 flex-1 flex flex-col justify-between">
                    <div>
                      {}
                      <div className="flex items-center justify-between mb-4">
                        <Badge variant="secondary" className="bg-secondary text-foreground text-xs font-medium border border-accent/20 px-2.5 py-0.5">
                          {item.accentLabel}
                        </Badge>
                        <span className="text-xs font-mono text-muted-foreground font-semibold">
                          0{item.id}
                        </span>
                      </div>

                      <h3 className="text-xl font-bold text-foreground font-serif mb-4">
                        {item.title}
                      </h3>

                      {}
                      <div className="rounded-lg bg-secondary/40 p-4 border border-accent/20 mb-4 space-y-2">
                        <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                          <Check className="size-4 text-emerald-600 shrink-0" />
                          <span>With YE Consultancy (Me)</span>
                        </div>
                        <p className="text-sm text-foreground/90 leading-relaxed font-sans">
                          {item.consultant}
                        </p>
                      </div>

                      {}
                      <div className="rounded-lg bg-muted/50 p-4 border border-border/60 space-y-2">
                        <div className="flex items-center gap-2 text-muted-foreground font-medium text-sm">
                          <X className="size-4 text-destructive/80 shrink-0" />
                          <span>Traditional Agency Model</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {item.agency}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-16 text-center bg-card rounded-xl p-8 sm:p-10 border border-border shadow-sm max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-foreground font-serif mb-3">
            Ready for a Direct, Result-Driven SEO Partnership?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto text-sm sm:text-base">
            Let's discuss your current organic reach, technical hurdles, and practical growth opportunities in a free, zero-pressure consultation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-base font-semibold">
              <Link to="/ContactPage">
                Book Your Free SEO Audit & Consultation
                <ArrowRight className="ml-2 size-5" />
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}